import Clarity from '@microsoft/clarity';
import { ClarityProjectKey, CLARITY_PROJECT_IDS } from './clarity-project-keys.enum';

/**
 * Utility functions for Microsoft Clarity integration
 */
export class ClarityUtils {
  /**
   * Get all available project keys
   * @returns Array of all available project keys
   */
  static getAvailableProjectKeys(): ClarityProjectKey[] {
    return Object.values(ClarityProjectKey);
  }

  /**
   * Get the project ID for a specific project key
   * @param projectKey - The project key to get the ID for
   * @returns Project ID or null if not configured
   */
  static getProjectIdForKey(projectKey: ClarityProjectKey): string | null {
    const projectId = CLARITY_PROJECT_IDS[projectKey];
    return projectId && projectId?.trim()?.length > 0 ? projectId : null;
  }

  /**
   * Validate that a project ID is properly configured
   * @param projectKey - The project key to validate
   * @returns boolean indicating if the project ID is configured
   */
  static isProjectIdConfigured(projectKey: ClarityProjectKey): boolean {
    return this.getProjectIdForKey(projectKey) !== null;
  }

  /**
   * Initialize Clarity with the official package
   * @param projectId - The Clarity project ID
   * @returns Promise that resolves when Clarity is initialized
   */
  static async initializeClarity(projectId: string): Promise<void> {
    if (!Clarity) {
      throw new Error('@microsoft/clarity package not available. Please install it with: yarn add @microsoft/clarity');
    }

    try {
      Clarity.init(projectId);
    } catch (error) {
      console.error('[ClarityUtils] Failed to initialize Clarity:', error);
      throw error;
    }
  }

  /**
   * Identify a user in Clarity using the official API
   * @param customId - The unique identifier for the customer (required)
   * @param customSessionId - A custom session identifier (optional)
   * @param customPageId - A custom page identifier (optional)
   * @param friendlyName - A friendly name for the customer (optional)
   * @returns boolean indicating if the call was successful
   */
  static identifyUser(
    customId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string
  ): boolean {
    if (!Clarity) {
      console.warn('[ClarityUtils] @microsoft/clarity package not available');
      return false;
    }

    try {
      Clarity.identify(customId, customSessionId, customPageId, friendlyName);
      return true;
    } catch (error) {
      console.error('[ClarityUtils] Error identifying user:', error);
      return false;
    }
  }

  /**
   * Set custom tags in Clarity using the official API
   * @param key - Tag key (required)
   * @param value - Tag value (required) - can be string or string array
   * @returns boolean indicating if the call was successful
   */
  static setTag(key: string, value: string | string[]): boolean {
    if (!Clarity) {
      console.warn('[ClarityUtils] @microsoft/clarity package not available');
      return false;
    }

    try {
      Clarity.setTag(key, value);
      return true;
    } catch (error) {
      console.error('[ClarityUtils] Error setting tag:', error);
      return false;
    }
  }

  /**
   * Track a custom event in Clarity
   * @param eventName - The name of the event (required)
   * @returns boolean indicating if the call was successful
   */
  static trackEvent(eventName: string): boolean {
    if (!Clarity) {
      console.warn('[ClarityUtils] @microsoft/clarity package not available');
      return false;
    }

    try {
      Clarity.event(eventName);
      return true;
    } catch (error) {
      console.error('[ClarityUtils] Error tracking event:', error);
      return false;
    }
  }

  /**
   * Set cookie consent for Clarity
   * @param consent - Whether consent is given (default: true)
   * @returns boolean indicating if the call was successful
   */
  static setCookieConsent(consent: boolean = true): boolean {
    if (!Clarity) {
      console.warn('[ClarityUtils] @microsoft/clarity package not available');
      return false;
    }

    try {
      Clarity.consent(consent);
      return true;
    } catch (error) {
      console.error('[ClarityUtils] Error setting cookie consent:', error);
      return false;
    }
  }

  /**
   * Upgrade the current session for priority recording
   * @param reason - The reason for the upgrade (required)
   * @returns boolean indicating if the call was successful
   */
  static upgradeSession(reason: string): boolean {
    if (!Clarity) {
      console.warn('[ClarityUtils] @microsoft/clarity package not available');
      return false;
    }

    try {
      Clarity.upgrade(reason);
      return true;
    } catch (error) {
      console.error('[ClarityUtils] Error upgrading session:', error);
      return false;
    }
  }
}
